import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Client behaviour for the `1-skio-plan-picker` block (pack-variant model).
 *
 * Each card is a Size/Supply *variant* carrying its own selling plan. Selecting
 * a card drives the (visually hidden) native Size/Supply variant option, so
 * Horizon's own fetch+morph updates the variant id, price block, media and
 * availability — no bespoke syncing. This component only:
 * - proxies a card click onto the matching native Size/Supply radio
 * - mirrors the selected variant's plan into the hidden `selling_plan` input
 *   (optimistically, so the Add-to-Cart label updates immediately; the server
 *   re-render keeps it authoritative)
 * - keeps `?selling_plan=` in sync (the native picker keeps `?variant=`)
 * - distributes each `_1-skio-plan` block (badge, perks, gift panels) from the
 *   hidden staging container into its matching plan frame, matched by interval
 * - handles the "One Time Purchase" link, which adds the single (1-packet)
 *   variant to the cart with no selling plan (quantity 1)
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} sellingPlanInput
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} [onetimeButton]
 *
 * @extends {Component<Refs>}
 */
class OnePlanPickerComponent extends Component {
  requiredRefs = ['sellingPlanInput', 'quantityInput'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#neutralizeIfDuplicate()) return;

    this.#abortController = new AbortController();

    this.#distributePlanContent();
    this.#syncSellingPlanFromChecked();

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Only one picker may drive a product form — a second instance would fight
   * over the form's hidden `selling_plan` input. The first picker in DOM order
   * wins; later ones are hidden and their form inputs disabled so they never
   * submit. In the theme editor the duplicate shows a note instead.
   * @returns {boolean} true when this instance is a neutralized duplicate
   */
  #neutralizeIfDuplicate() {
    const formId = this.dataset.formId;
    const first = document.querySelector(`one-plan-picker-component[data-form-id="${formId}"]`);
    if (!first || first === this) return false;

    for (const input of this.querySelectorAll('input')) input.disabled = true;

    if (window.Shopify?.designMode) {
      this.innerHTML =
        '<p style="margin: 0; padding: 12px 16px; border: 1px dashed currentColor; border-radius: 8px; font-size: 14px;">Only one Skio Plan Picker can be used per product page — this duplicate does nothing. Please remove it.</p>';
    } else {
      this.style.display = 'none';
    }

    console.warn('[1-skio-plan-picker] duplicate instance neutralized — use one plan picker per product form.');
    return true;
  }

  /**
   * Card selected — mirror its plan, mark it, and drive the native Size/Supply
   * variant option so Horizon re-renders the buy box.
   * @param {Event} event
   */
  handleTierChange(event) {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.disabled) return;

    // Set the plan on the form up front so the selection is carried even if the
    // shopper submits before Horizon's morph re-renders this block (the label
    // itself is server-rendered and refreshes with the morph).
    this.refs.sellingPlanInput.value = radio.dataset.planId || radio.value || '';
    this.refs.quantityInput.value = '1';
    this.#markSelected(radio);

    const url = new URL(window.location.href);
    if (radio.value) url.searchParams.set('selling_plan', radio.value);
    window.history.replaceState({}, '', url);

    // Drive the hidden native Size/Supply option -> fetch+morph the section.
    this.#selectNativeSupply(radio.dataset.optionValueId);
  }

  /**
   * Adds the single (1-packet) variant as a one-time purchase (no selling plan,
   * quantity 1) by submitting the product form with temporarily-overridden
   * inputs. The form handler reads FormData synchronously, so the values are
   * restored right after the submit event dispatches.
   */
  handleOneTimeAdd() {
    const form = this.#productForm();
    if (!form) return;

    const { sellingPlanInput, quantityInput } = this.refs;
    const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    const onetimeVariantId = this.refs.onetimeButton?.dataset.onetimeVariantId;

    const previousPlan = sellingPlanInput.value;
    const previousQuantity = quantityInput.value;
    const previousId = idInput?.value ?? null;

    sellingPlanInput.value = '';
    quantityInput.value = '1';
    if (idInput && onetimeVariantId) idInput.value = onetimeVariantId;

    form.requestSubmit();

    setTimeout(() => {
      sellingPlanInput.value = previousPlan;
      quantityInput.value = previousQuantity;
      if (idInput && previousId != null) idInput.value = previousId;
    }, 0);
  }

  /**
   * After a variant change Horizon re-renders the section with the correct card
   * checked and the server-side plan value — just re-distribute plan content and
   * mirror the plan into the form input.
   */
  #handleVariantUpdate = () => {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#distributePlanContent();
      this.#syncSellingPlanFromChecked();
    });
  };

  /**
   * Mirror the checked card's plan into the hidden `selling_plan` input. After a
   * server morph the correct card is already checked, so this keeps the form in
   * sync without re-triggering a variant change.
   */
  #syncSellingPlanFromChecked() {
    const checked = this.#checkedRadio();
    if (!checked) return;
    this.refs.sellingPlanInput.value = checked.dataset.planId || checked.value || '';
    this.refs.quantityInput.value = '1';
  }

  /**
   * Check the matching native Size/Supply radio and fire its change event so
   * Horizon's variant-picker fetches + morphs the section.
   * @param {string | undefined} optionValueId
   */
  #selectNativeSupply(optionValueId) {
    if (!optionValueId) return;
    const native = /** @type {HTMLInputElement | null} */ (
      document.querySelector(
        `variant-picker[data-template-product-match="true"] input[type="radio"][data-option-value-id="${optionValueId}"]`
      ) || document.querySelector(`variant-picker input[type="radio"][data-option-value-id="${optionValueId}"]`)
    );
    if (native && !native.checked) {
      native.checked = true;
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {HTMLInputElement} radio */
  #markSelected(radio) {
    const frame = radio.closest('[data-plan-frame]');
    for (const f of this.querySelectorAll('[data-plan-frame]')) {
      f.classList.toggle('1-plan-picker__plan--selected', f === frame);
    }
  }

  /**
   * Moves each `_1-skio-plan` content element into its matching plan frame, and
   * its perk list into the card's perks slot. Matching: delivery interval in
   * days (both sides normalized by Liquid). Idempotent — safe to re-run after
   * morphs. Unmatched or duplicate content stays hidden in the staging container.
   */
  #distributePlanContent() {
    const frames = Array.from(this.querySelectorAll('[data-plan-frame]'));
    const claimed = new Set();

    for (const content of this.querySelectorAll('[data-plan-content]')) {
      const days = content.dataset.planDays || '';
      if (days === '') continue;

      const frame = frames.find(
        (candidate) => !claimed.has(candidate) && candidate.dataset.intervalDays === days
      );
      if (!frame) continue;
      claimed.add(frame);

      if (content.parentElement !== frame) frame.appendChild(content);

      const perks = content.querySelector('[data-plan-perks]');
      const slot = frame.querySelector('[data-perks-slot]');
      if (perks && slot && perks.parentElement !== slot) slot.appendChild(perks);
    }
  }

  /** @returns {HTMLInputElement | null} */
  #checkedRadio() {
    return this.querySelector('input[type="radio"][data-variant-id]:checked');
  }

  /** @returns {HTMLFormElement | null} */
  #productForm() {
    const formId = this.dataset.formId;
    if (!formId) return null;
    const form = document.getElementById(formId);
    return form instanceof HTMLFormElement ? form : null;
  }
}

if (!customElements.get('one-plan-picker-component')) {
  customElements.define('one-plan-picker-component', OnePlanPickerComponent);
}
